import { PagedItems } from '@texas/types';
import { httpClient } from '../../httpClient/httpClient';
import {
  SearchArticlesResponse,
  VariantsSearchQueryRequest,
  SearchVariantsResponse,
  SearchVariantsCountResponse,
} from './searchVariantsTypes';
import {
  PaSearchQueryRequest,
  SearchPaCountResponse,
  SearchPaResponse,
} from './searchPaTypes';

export interface FilterItem {
  value: number;
  label: string;
  tags?: string[];
}

export interface SearchFilter {
  id: number;
  name: string;
}

export interface ExportArticleItem {
  id: number;
  variantId: number;
  customerId: number;
  branches: ExportArticleBranchItem[];
}

export interface ExportArticleBranchItem {
  id: number;
}

export interface SearchFilterQuery {
  searchTerm: string;
  page: number;
  pageSize: number;
  filteredOptions?: number[];
}

export interface SearchArticlesItem {
  id: number;
  name: string;
  itemCode: string;
  imageIdentifier: string;
}

export interface GlobalSearchRequest {
  searchTerm: string;
  resultsPerEntity?: number;
  includeArchived?: boolean;
}

export interface GlobalSearchArticleItem {
  id: number;
  itemCode: string;
  name: string;
  imageIdentifier: string;
  mainVariantId: number | null;
  syncedVariantId: number | null;
  productDevelopmentVariantId: number | null;
  nrOfPendingPa: number;
  nrOfActiveSamples: number;
  archived: boolean;
  isVariable: boolean;
  isMatrix: boolean;
}

export interface GlobalSearchProductApprovalItem {
  id: number;
  productApprovalNumber: string;
  productApprovalState: string;
  articleItemCode: string;
  articleId: number;
  imageIdentifier: string;
  isVariable: boolean;
  isMatrix: boolean;
}

export interface GlobalSearchCustomerItem {
  id: number;
  name: string;
  projectName: string | null;
  projectNumber: string | null;
  archived: boolean;
  logoIdentifier: string | null;
}

export interface GlobalSearchConceptItem {
  id: number;
  name: string;
  customerId: number;
  customerName: string;
  archived: boolean;
}

export interface GlobalSearchSupplierBranchInfo {
  branchErpId: string;
  supplierNumber: string;
}

export interface GlobalSearchSupplierItem {
  id: number;
  name: string;
  branchInfo: GlobalSearchSupplierBranchInfo[];
  archived: boolean;
}

export interface GlobalSearchInquiryItem {
  id: number;
  inquiryNumber: string;
  formattedInquiryNumber: string;
  articleId: number;
  articleItemCode: string;
  variantId: number;
  supplierName: string | null;
  archived: boolean;
  isVariable: boolean;
  isMatrix: boolean;
}

export interface GlobalSearchResponse {
  articles: GlobalSearchArticleItem[];
  productApprovals: GlobalSearchProductApprovalItem[];
  customers: GlobalSearchCustomerItem[];
  concepts: GlobalSearchConceptItem[];
  suppliers: GlobalSearchSupplierItem[];
  inquiries: GlobalSearchInquiryItem[];
}

export const searchApi = {
  searchArticles: (searchTerm: string) =>
    httpClient.get<SearchArticlesResponse>('search/articles', {
      params: { searchTerm },
    }),
  searchVariants: (params: VariantsSearchQueryRequest) =>
    httpClient.post<SearchVariantsResponse>(`search/variants`, {
      data: params,
    }),
  searchVariantsSlim: (params: VariantsSearchQueryRequest) =>
    httpClient.post<SearchVariantsResponse>(`search/variants-slim`, {
      data: params,
    }),
  searchVariantsCount: (params: VariantsSearchQueryRequest) =>
    httpClient.post<SearchVariantsCountResponse>(`search/variants/count`, {
      data: params,
    }),

  searchProductApprovals: (params: PaSearchQueryRequest) =>
    httpClient.post<SearchPaResponse>(`search/product-approvals`, {
      data: params,
    }),
  searchProductApprovalsCount: (params: PaSearchQueryRequest) =>
    httpClient.post<SearchPaCountResponse>(`search/product-approvals/count`, {
      data: params,
    }),

  searchBranches: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/branches', { data: query }),
  getBranches: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/branches/ids', { params: { ids } }),

  searchCategoryCodes: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/categorycodes', {
      data: query,
    }),
  getCategoryCodes: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/categorycodes/ids', {
      params: { ids },
    }),

  searchCustomers: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/customers', {
      data: query,
    }),
  getCustomers: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/customers/ids', { params: { ids } }),

  searchConcepts: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/concepts', { data: query }),
  getConcepts: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/concepts/ids', { params: { ids } }),

  searchStates: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/states', { data: query }),
  getStates: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/states/ids', { params: { ids } }),

  searchPaStates: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/product-approval-states', {
      data: query,
    }),
  getPaStates: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/product-approval-states/ids', {
      params: { ids },
    }),

  searchSuppliers: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/suppliers', {
      data: query,
    }),
  getSuppliers: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/suppliers/ids', {
      params: { ids },
    }),

  searchMaterials: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/materials', {
      data: query,
    }),
  getMaterials: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/materials/ids', {
      params: { ids },
    }),

  searchProcesses: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/processes', {
      data: query,
    }),
  getProcesses: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/processes/ids', { params: { ids } }),

  searchProductGroups: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/productgroups', {
      data: query,
    }),
  getProductGroups: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/productgroups/ids', {
      params: { ids },
    }),

  globalSearch: (request: GlobalSearchRequest) =>
    httpClient.post<GlobalSearchResponse>('search/global', { data: request }),
  searchTags: (query: SearchFilterQuery) =>
    httpClient.post<PagedItems<FilterItem>>('search/tags', { data: query }),
  getTags: (ids: number[]) =>
    httpClient.get<FilterItem[]>('search/tags/ids', { params: { ids } }),
};
